import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Izin, Kullanici } from '@nero/shared';
import { api, oturumDusunceCalistir, sayfaAnahtariniAyarla } from './api.js';

interface OturumDurumu {
  kullanici: Kullanici | null;
  izinler: Izin[];
  hazir: boolean;
  /** Oturum çalışırken düştü mü? Giriş ekranı bunu kullanıcıya açıklar. */
  suresiDoldu: boolean;
  girisYap: (kullaniciAdi: string, sifre: string) => Promise<void>;
  cikisYap: () => Promise<void>;
  yenile: () => Promise<void>;
  /** Arayüzü sadeleştirmek için; asıl kontrol her zaman sunucudadır. */
  yetkili: (izin: Izin) => boolean;
}

const Baglam = createContext<OturumDurumu | null>(null);

interface Yanit { kullanici: Kullanici | null; izinler: Izin[]; sayfa_anahtari?: string }

export function OturumSaglayici({ children }: { children: ReactNode }) {
  const [kullanici, setKullanici] = useState<Kullanici | null>(null);
  const [izinler, setIzinler] = useState<Izin[]>([]);
  const [hazir, setHazir] = useState(false);
  const [suresiDoldu, setSuresiDoldu] = useState(false);

  const yenile = useCallback(async () => {
    try {
      const y = await api.al<Yanit>('/oturum/ben');
      setKullanici(y.kullanici);
      setIzinler(y.izinler);
    } catch {
      setKullanici(null);
      setIzinler([]);
    } finally {
      setHazir(true);
    }
  }, []);

  useEffect(() => { void yenile(); }, [yenile]);

  /* Sayfa anahtarı SADECE girişte gelir ve yalnızca bellekte tutulur.
     Sayfa yenilenince kaybolur; bu yüzden her açılışta yeniden giriş istenir. */
  const girisYap = useCallback(async (kullaniciAdi: string, sifre: string) => {
    const y = await api.gonder<Yanit>('/oturum/giris', { kullanici_adi: kullaniciAdi, sifre });
    sayfaAnahtariniAyarla(y.sayfa_anahtari ?? '');
    setSuresiDoldu(false);
    setKullanici(y.kullanici);
    setIzinler(y.izinler);
  }, []);

  const cikisYap = useCallback(async () => {
    try { await api.gonder('/oturum/cikis'); } finally {
      sayfaAnahtariniAyarla('');
      setSuresiDoldu(false);
      setKullanici(null);
      setIzinler([]);
    }
  }, []);

  /* Sunucu 401 dönerse (oturum zaman aşımı, sunucu yeniden başladı) kullanıcıyı
     giriş ekranına al. Önceden her ekran anlamsız bir hata mesajı gösteriyordu. */
  useEffect(() => {
    oturumDusunceCalistir(() => {
      sayfaAnahtariniAyarla('');
      setSuresiDoldu(true);
      setKullanici(null);
      setIzinler([]);
    });
    return () => oturumDusunceCalistir(null);
  }, []);

  const deger = useMemo<OturumDurumu>(() => ({
    kullanici, izinler, hazir, suresiDoldu, girisYap, cikisYap, yenile,
    yetkili: (izin) => izinler.includes(izin),
  }), [kullanici, izinler, hazir, suresiDoldu, girisYap, cikisYap, yenile]);

  return <Baglam.Provider value={deger}>{children}</Baglam.Provider>;
}

export function useOturum(): OturumDurumu {
  const b = useContext(Baglam);
  if (!b) throw new Error('useOturum yalnızca OturumSaglayici içinde kullanılabilir');
  return b;
}

export function useYetki(izin: Izin): boolean {
  return useOturum().yetkili(izin);
}
